import axios, { Method } from "axios";
import compact from "lodash/compact";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import { Unit } from "../../types/customization/box";
import { SelectionAlbum, SelectionPhoto, UserInfos } from "../../types/selection/selection";
import BaseSelectionProvider from "./BaseSelectionProvider";

const VERSION = "v1";

export default class GoogleSelectionProvider extends BaseSelectionProvider {
  accessToken: string;
  albums: {
    data: SelectionAlbum[];
    paging?: string;
  };
  albumPhotos: {
    [albumRef: string]: {
      data: SelectionPhoto[];
      paging?: string;
    };
  };

  constructor(accessToken: string) {
    super();
    this.accessToken = accessToken;
    this.albums = { data: [] };
    this.albumPhotos = {};
  }

  hasAlbums(): boolean {
    return true;
  }

  hasMoreAlbums(): boolean {
    return !!this.albums.paging;
  }

  hasMoreAlbumPhotos(albumId: string): boolean {
    return !!this.albumPhotos[albumId] && !!this.albumPhotos[albumId].paging;
  }

  hasUserInfos(): boolean {
    return true;
  }

  async getAlbums(): Promise<SelectionAlbum[]> {
    if (this.albums.data.length > 0) {
      return this.albums.data;
    }
    this.albums.data = await this.getAlbumsDistant();
    return this.albums.data;
  }

  async getMoreAlbums(): Promise<SelectionAlbum[]> {
    if (!this.hasMoreAlbums()) {
      return this.albums.data;
    }
    const nextAlbums = await this.getAlbumsDistant(true);
    this.albums.data = this.albums.data.concat(nextAlbums);
    return this.albums.data;
  }

  async getAlbumsDistant(next = false): Promise<SelectionAlbum[]> {
    const params: { pageSize: number; pageToken?: string } = {
      pageSize: 50,
    };

    if (next && this.hasMoreAlbums()) {
      params.pageToken = this.albums.paging;
    }

    let latestPhotos: AlbumPhotoResponse = {
      mediaItems: [],
    };
    if (!next) {
      latestPhotos = await this.sendRequest<AlbumPhotoResponse>({
        edge: "mediaItems",
        params,
      });
    }

    const albumResponse = await this.sendRequest<AlbumResponse>({
      edge: "albums",
      params,
    });

    if (latestPhotos && latestPhotos.mediaItems && latestPhotos.mediaItems.length) {
      albumResponse.albums = isEmpty(albumResponse) ? [] : albumResponse.albums;
      albumResponse.albums.unshift({
        coverPhotoBaseUrl: latestPhotos.mediaItems[0].baseUrl,
        id: "__all_photos__",
        mediaItemsCount: latestPhotos.mediaItems.length,
        title: "All photos",
      });
    }

    this.albums.paging = albumResponse.nextPageToken ? albumResponse.nextPageToken : undefined;

    return compact(
      map(albumResponse.albums, (datum) => {
        if (!datum.coverPhotoBaseUrl || datum.mediaItemsCount === 0) {
          return;
        }

        return {
          coverPictureUrl: datum.coverPhotoBaseUrl,
          name: datum.title,
          ref: datum.id,
        };
      })
    );
  }

  async getAlbumPhotos(albumId: string): Promise<SelectionPhoto[]> {
    if (this.albumPhotos[albumId] && this.albumPhotos[albumId].data.length > 0) {
      return this.albumPhotos[albumId].data;
    }
    this.albumPhotos[albumId] = { data: [] };
    this.albumPhotos[albumId].data = await this.getAlbumPhotosDistant(albumId);
    return this.albumPhotos[albumId].data;
  }

  async getMoreAlbumPhotos(albumId: string): Promise<SelectionPhoto[]> {
    if (!this.hasMoreAlbumPhotos(albumId)) {
      return this.albumPhotos[albumId].data;
    }
    const nextAlbums = await this.getAlbumPhotosDistant(albumId, true);
    this.albumPhotos[albumId].data = this.albumPhotos[albumId].data.concat(nextAlbums);
    return this.albumPhotos[albumId].data;
  }

  async getAlbumPhotosDistant(albumId: string, next = false): Promise<SelectionPhoto[]> {
    const params: { albumId?: string; pageSize: number; pageToken?: string } = {
      albumId,
      pageSize: 50,
    };

    if (next && this.hasMoreAlbumPhotos(albumId)) {
      params.pageToken = this.albumPhotos[albumId].paging;
    }

    if (albumId === "__all_photos__") {
      delete params.albumId;
    }

    const albumPhotoResponse = await this.sendRequest<AlbumPhotoResponse>({
      edge: "mediaItems:search",
      method: "POST",
      params,
    });

    this.albumPhotos[albumId].paging = albumPhotoResponse.nextPageToken;

    return filter(
      albumPhotoResponse.mediaItems,
      (album) => album.mediaMetadata.photo !== undefined
    ).map((datum) => {
      return {
        key: `google-${datum.id}`,

        height: {
          unit: Unit.PX,
          value: parseInt(datum.mediaMetadata.height, 10),
        } as const,
        width: {
          unit: Unit.PX,
          value: parseInt(datum.mediaMetadata.width, 10),
        } as const,

        provider: "google_photos",
        providerRef: datum.id,

        providerPictureUrl: `${datum.baseUrl}=w${datum.mediaMetadata.width}-h${datum.mediaMetadata.height}`,
        providerThumbnailUrl: datum.baseUrl,
      };
    });
  }

  async getUserInfos(): Promise<UserInfos> {
    const url = "https://www.googleapis.com/oauth2/v1/userinfo";
    const response = await axios.get<UserInfosResponse>(url, {
      headers: {
        Authorization: "Bearer " + this.accessToken,
      },
      params: {
        alt: "json",
        oauth_token: this.accessToken,
      },
    });
    const userInfos = {
      email: response.data.email,
      picture: response.data.picture,
    };
    return userInfos;
  }

  async sendRequest<R>({
    edge,
    params,
    method = "GET",
  }: {
    edge: string;
    params: Record<string, unknown>;
    method?: Method;
  }): Promise<R> {
    const url = `https://photoslibrary.googleapis.com/${VERSION}/${edge}`;
    try {
      const response: { data: Promise<R> } = await axios(url, {
        headers: {
          Authorization: "Bearer " + this.accessToken,
        },
        method,
        params: {
          ...params,
        },
      });
      return response.data;
    } catch (e: unknown) {
      const error = e as { response: unknown };
      return (typeof error === "object" ? error?.response : error) as R;
    }
  }
}

interface AlbumResponse {
  albums: Array<{
    mediaItemsCount: number;
    coverPhotoBaseUrl?: string;
    id: string;
    title: string;
  }>;
  nextPageToken?: string;
}

interface AlbumPhotoResponse {
  mediaItems: Array<{
    id: string;
    productUrl: string;
    baseUrl: string;
    mediaMetadata: AlbumPhotoResponseData;
  }>;
  nextPageToken?: string;
}

interface AlbumPhotoResponseData {
  photo?: Record<string, unknown>;
  height: string;
  width: string;
}

interface UserInfosResponse {
  email?: string;
  picture?: string;
}
